import React from 'react';
import { Link } from 'react-router-dom';
import { Linkedin, Twitter, Youtube, Github, Globe } from 'lucide-react';

const PLATFORM_LINKS = [
  { label: 'Overview',      href: '/platform' },
  { label: 'Observability', href: '/platform/observability' },
  { label: 'Self Healing',  href: '/platform/self-healing' },
  { label: 'AI & Agents',   href: '/platform/ai-agents' },
];

const SOLUTIONS_LINKS = [
  { label: 'Networking & NW Security',         href: '/solutions/networking' },
  { label: 'Dc compute',                       href: '/solutions/datacenter' },
  { label: 'Cybersecurity',                    href: '/solutions/cybersecurity' },
  { label: 'One GRC',                          href: '/solutions/grc' },
  { label: 'ITSM & ITOM',                      href: '/solutions/itsm-itom' },
  { label: 'Enterprise Observability',         href: '/solutions/observability' },
  { label: 'AI Factory',                       href: '/solutions/ai-factory' },
  { label: 'NOC & SOC',                        href: '/solutions/noc-soc' },
];

const SERVICES_LINKS = [
  { label: 'Consultancy',    href: '/services/consult' },
  { label: 'Assessment',     href: '/services/assess' },
  { label: 'Implementation', href: '/services/implement' },
  { label: 'Operation',      href: '/services/operate' },
  { label: 'Governance',     href: '/services/govern' },
  { label: 'Automation',     href: '/services/automate' },
  { label: 'Optimization',   href: '/services/optimize' },
  { label: 'Transformation', href: '/services/transform' },
];

const RESOURCES_LINKS = [
  { label: 'Insights & Reports', href: '/insights' },
  { label: 'Blog',               href: '/blog' },
  { label: 'Webinars & Events',  href: '/events' },
  { label: 'Documentation',      href: '/docs' },
  { label: 'Partner Portal',     href: '/partner-portal' },
  { label: 'Employee Portal',    href: '/employee-portal' },
];

const FooterColumn = ({ heading, links }) => (
  <div>
    <p className="text-white font-semibold text-[14px] mb-4">{heading}</p>
    <ul className="space-y-2.5">
      {links.map((item) => {
        const cls = "text-[13px] text-neutral-400 hover:text-white transition-colors text-left";
        if (item.onClick) {
          return (
            <li key={item.label}>
              <button type="button" onClick={item.onClick} className={cls} data-testid={`footer-link-${item.label.toLowerCase().replace(/[^a-z0-9]+/g, '-')}`}>
                {item.label}
              </button>
            </li>
          );
        }
        const isInternal = item.href.startsWith('/');
        return (
          <li key={item.label}>
            {isInternal ? (
              <Link to={item.href} className={cls} data-testid={`footer-link-${item.label.toLowerCase().replace(/[^a-z0-9]+/g, '-')}`}>{item.label}</Link>
            ) : (
              <a href={item.href} className={cls} data-testid={`footer-link-${item.label.toLowerCase().replace(/[^a-z0-9]+/g, '-')}`}>{item.label}</a>
            )}
          </li>
        );
      })}
    </ul>
  </div>
);

const Footer = () => {
  // Company links wire to relevant destinations.
  const COMPANY_LINKS = [
    { label: 'About',      href: '/about' },
    { label: 'Leadership', href: '/about' },
    { label: 'Careers',    href: '/careers' },
  ];

  return (
    <footer className="bg-neutral-950 text-neutral-300">
      <div className="max-w-[1400px] mx-auto px-6 lg:px-10 py-16">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-14">
          {/* Brand */}
          <div className="lg:col-span-3">
            <div className="flex items-center gap-3">
              <img
                src="https://customer-assets.emergentagent.com/job_metaxuone-preview/artifacts/kjo0ib4e_Logo.png"
                alt="MetaxuOne"
                className="h-9 w-9 object-contain flex-shrink-0"
              />
              <img
                src="https://customer-assets.emergentagent.com/job_metaxuone-preview/artifacts/ir3od9nj_company%20name.jpg"
                alt="METAXUONE"
                className="h-5 w-auto max-w-[150px] object-contain"
                style={{ mixBlendMode: 'screen' }}
              />
            </div>
            <p className="text-[13px] text-neutral-400 mt-4 leading-relaxed max-w-[280px]">
              G-15, Plot 1-A/B,C, Shreeji Height, Sector 46A, Darave, Thane – 400706, Maharashtra, India.
            </p>
            <div className="flex gap-3 mt-6">
              {[Linkedin, Twitter, Youtube, Github].map((I, i) => (
                <a key={i} href="#" className="w-9 h-9 rounded-full border border-neutral-800 hover:border-blue-500 flex items-center justify-center transition-colors">
                  <I className="w-4 h-4" />
                </a>
              ))}
            </div>
          </div>

          {/* Link columns */}
          <div className="lg:col-span-9">
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-5 gap-x-8 gap-y-10">
              <FooterColumn heading="Platform"  links={PLATFORM_LINKS} />
              <FooterColumn heading="Solutions" links={SOLUTIONS_LINKS} />
              <FooterColumn heading="Services"  links={SERVICES_LINKS} />
              <FooterColumn heading="Resources" links={RESOURCES_LINKS} />
              <FooterColumn heading="Company"   links={COMPANY_LINKS} />
            </div>
          </div>
        </div>

        <div className="mt-14 pt-8 border-t border-neutral-900 flex flex-col md:flex-row items-start md:items-center justify-between gap-4">
          <p className="text-[12px] text-neutral-500">© {new Date().getFullYear()} MetaxuOne Private Limited. All rights reserved.</p>
          <div className="flex flex-wrap items-center gap-5 text-[12px] text-neutral-500">
            <Link to="/privacy" className="hover:text-white transition-colors">Privacy</Link>
            <Link to="/terms" className="hover:text-white transition-colors">Terms</Link>
            <Link to="/cookies" className="hover:text-white transition-colors">Cookies</Link>
            <Link to="/imprint" className="hover:text-white transition-colors">Imprint</Link>
            <button className="inline-flex items-center gap-1.5 hover:text-white transition-colors">
              <Globe className="w-3.5 h-3.5" /> English (US)
            </button>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
